// WS-WHITELABEL-TENANT-SCOPE-1 — a white-label host sees ONE org and ONE
// workspace, and nothing else.
//
// ── WHY THIS EXISTS ────────────────────────────────────────────────────────
// A branded host names its customer in DNS. Everything else about the session
// is still account-wide: `orgs` carries every organisation the signed-in user
// belongs to (17, on the account this was measured with), and any code path
// that falls back to "the session's ambient org/workspace" can therefore land
// on a DIFFERENT customer under this customer's own domain.
//
// That is not hypothetical. Measured on gita.pr.symbols.app 2026-08-31, with
// GITA present in `orgs` the whole time:
//   root.workspace._id  69e54008…  displayName "Moll"  org "community"
// The org switcher read "Symbols Community", the nav was the default
// Studio/Tasks/Files, and none of GITA's apps rendered. Sampled every 60ms for
// 10s: it never corrected. On other loads the same URL resolved GITA — the
// outcome is a race, which is why it reads as "renders another workspace, then
// re-renders to mine".
//
// The existing mitigations all CORRECT state after the fact: customDomainBoot
// resolves the host and navigates (inert with the MWT flag off), or pins the
// org through the SDK. Correcting after the fact is what produces the flash,
// the race, and the loads that never converge.
//
// This function removes the ambient alternative instead. Applied to the boot
// snapshot — the single object every downstream consumer reads for orgs,
// workspaces, the active org and the active workspace — there is no other
// tenant left to fall back TO. Downstream code needs no guard of its own,
// which is the property that makes this hold as new surfaces are added.
//
// ── FAIL CLOSED ────────────────────────────────────────────────────────────
// If the signed-in user is NOT a member of the host's org, this returns an
// EMPTY scope, never their own orgs. A stranger on a customer's domain must
// see nothing — falling back to "their" content is exactly the wrong-tenant
// render this exists to stop, just with the tenants swapped.
//
// The empty scope is expressed with the keys the snapshot ALREADY has
// (`orgs: []`, `org: null`, `workspace: null`) and no new one. An earlier cut
// added a `whitelabelNoAccess` flag; it is deliberately gone. The boot
// snapshot is a CLOSED shape — `__tests__/bootAuthSnapshotClosedShape.test.js`
// enforces that every key either builder emits is allowlisted AND classified
// in state.js — and because that guard reads bootShell.js as TEXT, a key
// introduced HERE would ride the `{ ...restAuth }` spread into root.update
// while remaining invisible to it. Evading a guard is not passing it. A
// no-access SURFACE should add the key deliberately, through the allowlist
// and the classification, at the point something actually renders it.
//
// ── NOT A UI FILTER ────────────────────────────────────────────────────────
// Hiding other organisations in the switcher is a consequence of this, not the
// mechanism. A UI-only filter would leave the wrong tenant loaded and merely
// invisible — still billed, still fetched, still rendered underneath.
//
// Pure by construction: no imports, no I/O, no globals. The whole contract is
// unit-testable without booting the shell (the `isAuthRejection` precedent in
// bootShell.js).

const idOf = (row) => (row ? String(row.id || row._id || '') : '')

const orgIdOfWorkspace = (w) => String((w && (w.org_id || w.organization)) || '')

/**
 * The workspace rows a white-label host may show: its ONE workspace.
 *
 * WHITELABEL-STRICT-TENANT-SCOPE-1 — a host's verified row names exactly one
 * workspace, and the server locks every request from that host's origin to
 * it. Listing the org's OTHER workspaces here (the earlier cut kept every
 * workspace of the org) offered switches the server now refuses, and is the
 * same cross-workspace disclosure one level down. When the host names no
 * workspace (an older server payload) the org's rows remain, filtered by org.
 *
 * `hostScope === false` is the seed's "tenant could not be established"
 * verdict → no rows. No host scope at all → the list untouched (platform).
 */
export const scopeWorkspaceListToWhitelabelHost = (list, hostScope) => {
  if (!Array.isArray(list)) return list
  if (hostScope === false) return []
  const orgId = hostScope && hostScope.organizationId ? String(hostScope.organizationId) : ''
  if (!orgId) return list
  const inOrg = list.filter((w) => orgIdOfWorkspace(w) === orgId)
  const wantedWs = hostScope.workspaceId ? String(hostScope.workspaceId) : ''
  return wantedWs ? inOrg.filter((w) => idOf(w) === wantedWs) : inOrg
}

/**
 * Scope a boot snapshot to the white-label host's single tenant.
 *
 * @param {object} snapshot  the boot snapshot: { orgs, workspaces, org,
 *                           workspace, jwtClaims, ... }
 * @param {object|false} hostScope { organizationId, workspaceId } as resolved
 *                           from the host — `context.state.whitelabelHostScope`,
 *                           which `seedWhitelabelHostScope` has already awaited
 *                           before bootShell runs, so this needs no fetch of
 *                           its own. `false` = a customer host whose tenant
 *                           could not be established: no tenant at all.
 * @returns {object} the snapshot, scoped. Returned UNTOUCHED when there is no
 *                   host scope, so platform hosts are byte-identical to today.
 */
export const scopeSnapshotToWhitelabelHost = (snapshot, hostScope) => {
  if (!snapshot || typeof snapshot !== 'object') return snapshot
  // A customer host that could not prove its tenant renders NONE — never the
  // account's ambient one (WHITELABEL-STRICT-TENANT-SCOPE-1). Same empty
  // shape as the non-member case below.
  if (hostScope === false) {
    return { ...snapshot, orgs: [], workspaces: [], org: null, workspace: null }
  }
  const orgId = hostScope && hostScope.organizationId ? String(hostScope.organizationId) : ''
  // No host scope: a platform host. Untouched — this must never degrade the
  // platform experience to fix the branded one.
  if (!orgId) return snapshot

  const orgs = Array.isArray(snapshot.orgs) ? snapshot.orgs : []
  const hostOrg = orgs.find((o) => idOf(o) === orgId) || null

  // Not a member. Empty, never their own orgs — see FAIL CLOSED above.
  if (!hostOrg) {
    return {
      ...snapshot,
      orgs: [],
      workspaces: [],
      org: null,
      workspace: null
    }
  }

  // The host's workspace ONLY (or, when the host names none, this org's). A
  // workspace row carries `org_id` (bootShell's own `workspace?.org_id ||
  // claims.workspace_org_id` precedent).
  const workspaces = scopeWorkspaceListToWhitelabelHost(
    Array.isArray(snapshot.workspaces) ? snapshot.workspaces : [],
    hostScope
  )

  // The host's workspace. When the host names one that is not in the list
  // the user has no membership there: no workspace, never another one. The
  // first-in-org fallback survives only for a host that names none.
  const wantedWs = hostScope.workspaceId ? String(hostScope.workspaceId) : ''
  const workspace = wantedWs
    ? workspaces.find((w) => idOf(w) === wantedWs) || null
    : workspaces[0] || null

  // Keep the claims consistent with what was just pinned. Leaving a stale
  // `active_workspace_id` here is precisely how the wrong workspace re-enters
  // downstream (WS-ACTIVE-WORKSPACE-ID-SHAPE-1 read exactly this claim).
  const jwtClaims = { ...(snapshot.jwtClaims || {}) }
  jwtClaims.workspace_org_id = orgId
  if (workspace) jwtClaims.active_workspace_id = idOf(workspace)

  return {
    ...snapshot,
    orgs: [hostOrg],
    workspaces,
    org: hostOrg,
    workspace,
    jwtClaims
  }
}

export default scopeSnapshotToWhitelabelHost

/**
 * Scope the JWT CLAIMS to the white-label host's single tenant.
 *
 * ── WHY A SECOND ENTRY POINT ───────────────────────────────────────────────
 * `scopeSnapshotToWhitelabelHost` fixes the BOOT snapshot, and that closed the
 * wrong-tenant RENDER. It does not survive the auth tick: on `userChanged` or
 * `claimsChanged`, packages/workspace/functions/auth.js rebuilds `claims.orgs`
 * from Mongo and writes `{ orgs, workspaces, org, jwtClaims }` to root in ONE
 * update — overwriting the scoped snapshot with the account-wide list.
 *
 * Measured on gita.pr.symbols.app after the boot fix shipped: `root.workspace`
 * was correctly GITA, and `root.orgs.length` was 17 with
 * `jwtClaims.active_workspace_id` still naming another org's workspace. One
 * update call, four keys, all unscoped.
 *
 * The tick derives `orgsList`, `workspacesAll`, `activeOrgId` and `activeOrg`
 * from `nextClaims`, and writes `nextClaims` itself as `jwtClaims`. So scoping
 * the CLAIMS once, before any of that is derived, covers every one of them —
 * one intervention instead of four that can drift apart.
 *
 * Same contracts as its sibling: pure, no host scope means the claims are
 * returned untouched BY IDENTITY, and a non-member gets an EMPTY org list
 * rather than their own.
 *
 * @param {object} claims     the tick's `nextClaims`
 * @param {object} hostScope  `{ organizationId, workspaceId }` — on the auth
 *                            tick this is `root.whitelabelBrand`, which the
 *                            pre-mount seed has already populated.
 */
export const scopeClaimsToWhitelabelHost = (claims, hostScope) => {
  if (!claims || typeof claims !== 'object') return claims
  // The seed's "tenant could not be established" verdict: the tick derives
  // `orgsList` / `workspacesAll` from these two arrays, so emptying them is
  // what keeps every org the account belongs to off this customer's domain
  // (WHITELABEL-STRICT-TENANT-SCOPE-1).
  if (hostScope === false) return { ...claims, orgs: [], workspaces: [] }
  const orgId = hostScope && hostScope.organizationId ? String(hostScope.organizationId) : ''
  if (!orgId) return claims

  const hasOrgs = Array.isArray(claims.orgs)
  const hasWorkspaces = Array.isArray(claims.workspaces)
  if (!hasOrgs && !hasWorkspaces) return claims

  const out = { ...claims }
  if (hasOrgs) out.orgs = claims.orgs.filter((o) => idOf(o) === orgId)
  // The host's ONE workspace — see scopeWorkspaceListToWhitelabelHost.
  if (hasWorkspaces)
    out.workspaces = scopeWorkspaceListToWhitelabelHost(claims.workspaces, hostScope)

  // `out.orgs` IS the membership test, and it has already run. An empty list
  // means this user has no business on this host — so nothing else is handed
  // to them either. Leaving the host org's workspaces in place would be a
  // list of another tenant's rooms given to a stranger, which is the same
  // disclosure this function exists to close, one level down. (Real claims
  // for a non-member would not carry them; this is the defensive half, and
  // it is the case the test caught.)
  const isMember = hasOrgs && out.orgs.length > 0
  if (hasOrgs && !isMember) {
    if (hasWorkspaces) out.workspaces = []
    return out
  }

  // Pinning is for members only: naming a concrete workspace id to a
  // non-member would hand them a target. When the claims carry no org list at
  // all there is nothing to test membership against, so nothing is pinned.
  if (isMember) {
    const wanted = hostScope.workspaceId ? String(hostScope.workspaceId) : ''
    const fromList = hasWorkspaces
      ? (wanted && out.workspaces.find((w) => idOf(w) === wanted)) || out.workspaces[0] || null
      : null
    // Prefer a workspace VALIDATED against the scoped list; fall back to the
    // host's own id, which the server resolved for this domain. Either way it
    // cannot name another tenant: the list is already filtered, and the host
    // id belongs to the host's org by construction.
    const pinned = fromList ? idOf(fromList) : wanted
    if (pinned) out.active_workspace_id = pinned
    out.workspace_org_id = orgId
  }

  return out
}
